import { omit, set, cloneDeep } from 'lodash';

export const ATTRIBUTE_SLIDER_SETTINGS = 'slider_settings';
export const ATTRIBUTE_TAB_NAV_SETTINGS = 'tabNavSliderSettings';
export const ATTRIBUTE_TAB_CONTENT_SETTINGS = 'tabContentSliderSettings';
export const FIELD_TYPES = {
  Range: 'range',
  Toggle: 'toggle',
  Number: 'number',
  Select: 'select',
  Text: 'text',
};

/**
 *
 * @param {Object}   attributes    Block attributes
 * @param {Function} setAttributes Function for change attributes
 * @param {*}        attribute     Specific block attribute
 * @param {string}   layout        Specific block attribute layout (Mobile, Tablet, Table Landscape and Desktop)
 * @param {string}   property      Specific block attribute key
 * @param {string}   childProperty Specific block attribute key
 * @param {*}        value         Specific block attribute value
 */
export const setSliderSettingAttribute = (
  attributes,
  setAttributes,
  attribute,
  layout,
  property,
  childProperty,
  value,
) => {
  const updatedAttributes = cloneDeep(attributes);

  const setAttribute = (attr, lay, prop, childProp, val) => {
    let propertyPath = `${attr}.${lay}.${prop}`;
    if (childProp) {
      propertyPath += `.${childProp}`;
    }
    set(updatedAttributes, propertyPath, val);
  };

  if (Array.isArray(attribute)) {
    attribute.forEach(
      ({ attribute, layout, property, childProperty, value }) => {
        setAttribute(attribute, layout, property, childProperty, value);
      },
    );
  } else {
    setAttribute(attribute, layout, property, childProperty, value);
  }

  setAttributes(updatedAttributes);
};

/**
 *
 * @param {Object}   attributes        Block attributes
 * @param {Function} setAttributes     Function for change attributes
 * @param {*}        attribute         Specific block attribute
 * @param {string}   layout            Specific block attribute layout (Mobile, Tablet, Table Landscape and Desktop)
 * @param {string}   attributeKey      Specific block attribute key
 * @param {string}   attributeChildKey Specific block attribute key
 */
export const removeSliderSettingAttribute = (
  attributes,
  setAttributes,
  attribute,
  layout,
  attributeKey,
  attributeChildKey,
) => {
  let updatedAttributes = {};

  const removeAttribute = (attr, lay, prop, childProp) => {
    let propertyPath = `${attr}.${lay}.${prop}`;
    if (childProp) {
      propertyPath += `.${childProp}`;
    }
    updatedAttributes = omit(attributes, propertyPath);
  };

  if (Array.isArray(attribute)) {
    attribute.forEach(
      ({ attribute, layout, attributeKey, attributeChildKey }) => {
        removeAttribute(attribute, layout, attributeKey, attributeChildKey);
      },
    );
  } else {
    removeAttribute(attribute, layout, attributeKey, attributeChildKey);
  }

  setAttributes(updatedAttributes);
};

/**
 * Display progress bar for slider and tab slider
 *
 * @param {HTMLElement} swiperElm      Slider html element (data-swiper)
 * @param {Object}      swiperInstance Slider instance
 */
export const displayProgressBar = (swiperElm, swiperInstance) => {
  const fragmentElm = swiperElm.querySelector('.swiper-fraction');
  const progressBarElm = swiperElm.querySelector(
    '.swiper-pagination-progressbar',
  );

  if (progressBarElm) {
    const totalSlide = swiperInstance.slides.length;
    const snapGrid = swiperInstance.snapGrid.length;
    const current = (swiperInstance.realIndex + 1) % totalSlide || totalSlide;
    fragmentElm.textContent = `${current}/${snapGrid}`;
  }
};

/**
 * Remove swiper class
 *
 * @param {HTMLElement} swiperElm Slider html element (data-swiper)
 */
export const removeSwiperClasses = (swiperElm) => {
  swiperElm.classList.remove(
    'swiper-backface-hidden',
    'swiper-navigation-disabled',
    'swiper-pagination-disabled',
    'swiper-scrollbar-disabled',
  );
  swiperElm.classList.add('swiper-destroyed');
};

/**
 * Slider destroy
 *
 * @param {HTMLElement} swiperElm Slider html element (data-swiper)
 */
export const sliderDestroy = (swiperElm) => {
  removeSwiperClasses(swiperElm);
};

/**
 * Slider on change function
 *
 * @param {HTMLElement} swiperElm      Slider html element (data-swiper)
 * @param {Object}      swiperInstance Slider instance
 */
export const sliderOnChange = (swiperElm, swiperInstance) => {
  displayProgressBar(swiperElm, swiperInstance);
};

/**
 * Slider init function
 *
 * @param {HTMLElement} swiperElm      Slider html element (data-swiper)
 * @param {Object}      swiperInstance Slider instance
 */
export const sliderInit = (swiperElm, swiperInstance) => {
  displayProgressBar(swiperElm, swiperInstance);
};

/**
 * Slider before init function
 *
 * @param {HTMLElement} swiperElm Slider html element (data-swiper)
 */
export const sliderBeforeInit = (swiperElm) => {
  swiperElm.classList.remove('swiper-destroyed');
  const paginationElm = swiperElm.querySelector('.swiper-pagination');
  paginationElm.classList.remove(
    'swiper-pagination-clickable',
    'swiper-pagination-bullets',
    'swiper-pagination-horizontal',
    'swiper-pagination-progressbar',
  );
};
